import "./App.css";

import { ThemeProvider } from "@mui/material";
import jwt_decode from "jwt-decode";
import { useEffect, useState } from "react";
import { RawIntlProvider } from "react-intl";
import { Provider } from "react-redux";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "./app/hooks";
import { store } from "./app/store";
import ProtectedRoute from "./common/components/AuthProtectedRoute/AuthProtectedRoute";
import { MessageBar } from "./common/components/MessageBar/MessageBar";
import { createIntlModule, LanguageParam } from "./common/language";
import { theme } from "./common/styles/theme";
import { DecodedJwt } from "./common/types/DecodedJWT";
import {
  closeMessageBar,
  selectMessageBarMessage,
  selectMessageBarOpenState,
  selectMessageBarStatus,
} from "./features/messageBar/messageBarSlice";
import {
  fetchQuestionnairesAsync,
  fetchUserResponsesAsync,
  selectStateStatus,
} from "./features/questionnaire/questionnaireSlice";
import {
  login,
  logout,
  selectClientId,
  selectLanguage,
  selectToken,
  setClientId,
  setLanguage,
} from "./features/user/userSlice";
import { Dashboard } from "./pages/Dashboard/Dashboard";
import { Questionnaire } from "./pages/Questionnaire/Questionnaire";

const App = () => {
  const ALLOW_ORIGIN = process.env.REACT_APP_MEMBER_PORTAL_ORIGIN;
  const isMessageBarOpen = useAppSelector(selectMessageBarOpenState);
  const messageString = useAppSelector(selectMessageBarMessage);
  const messageStatus = useAppSelector(selectMessageBarStatus);
  const dispatch = useAppDispatch();

  const [hraData, setHraData] = useState("");

  const clientId = useAppSelector(selectClientId);
  const language = useAppSelector(selectLanguage) as LanguageParam;
  const status = useAppSelector(selectStateStatus);
  const token = useAppSelector(selectToken);

  const props = {
    hra_data: hraData,
    client_id: clientId || "",
    language: language || "",
  };

  const intl = createIntlModule(language || "en");

  const getAttributeValues = (attributes: NamedNodeMap) => {
    const hraDataParam = attributes.getNamedItem("hra_data")?.value || "";
    setHraData(hraDataParam);

    const clientIdParam = parseInt(
      attributes.getNamedItem("client_id")?.value as string
    );
    if (clientIdParam) {
      dispatch(setClientId(clientIdParam));
    }

    const languageValue = attributes.getNamedItem("language")?.value?.toLowerCase();
    let languageParam = (languageValue ?? "en").toLowerCase() || "en";
    languageParam = languageParam === "en-us" ? "en" : languageParam;

    if (languageParam) {
      dispatch(setLanguage(languageParam));
    }

    return {
      hraData: hraDataParam,
      clientId: clientIdParam,
      language: languageParam,
    };
  };

  type CheckAuthParams = {
    hraData: string;
    clientId: number;
    language: string;
  };

  useEffect(() => {
    const checkAuthentication = async (authParams: CheckAuthParams) => {
      try {
        const decoded: DecodedJwt = jwt_decode(authParams.hraData);
        const nowTimestamp = new Date().getTime() / 1000;
        if (decoded.exp <= nowTimestamp) {
          dispatch(logout());
        } else {
          await dispatch(
            login({ token: authParams.hraData, patientId: decoded.aud })
          );
          getUserData(authParams);
        }
      } catch (e) {
        dispatch(logout());
      }
    };

    const getUserData = async (params: CheckAuthParams) => {
      if (status === "idle") {
        await dispatch(
          fetchQuestionnairesAsync({
            ...(params.clientId && { client_id: params.clientId }),
            ...(params.language && { language: params.language }),
            status: "active",
          })
        );
        await dispatch(fetchUserResponsesAsync());
      }
    };

    // add event listener for the member web app to use iframe
    const addParams = (event: any) => {
      if (event.origin !== ALLOW_ORIGIN) return;
      if (event.data.type === "set_hra_data") {
        document
          .querySelector("[hra_data]")
          ?.setAttribute("hra_data", event.data.token);
        document
          .querySelector("[client_id]")
          ?.setAttribute("client_id", event.data.clientId);
        document
          .querySelector("[language]")
          ?.setAttribute("language", event.data.language);
      }
    };

    window.addEventListener("message", addParams);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation: any) => {
        if (mutation.type === "attributes") {
          const el: any = mutation as any;
          const mut: Element = el.target as Element;
          const newParams = getAttributeValues(mut.attributes);
          createIntlModule(newParams.language as LanguageParam);

          if (token !== mut.attributes[0].value && status !== "loading") {
            checkAuthentication(newParams);
          }
          if (status !== "loading") {
            getUserData(newParams);
          }
        }
      });
    });
    observer.observe(document.querySelector("[hra_data]")!, {
      attributes: true, //configure it to listen to attribute changes
    });
    observer.observe(document.querySelector("[client_id]")!, {
      attributes: true,
    });
    observer.observe(document.querySelector("[language]")!, {
      attributes: true,
    });
    return () => {
      window.removeEventListener("message", addParams);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, clientId, language, token, hraData, dispatch, ALLOW_ORIGIN]);

  return (
    <RawIntlProvider value={intl}>
      <div {...props}>
        <Provider store={store}>
          <ThemeProvider theme={theme}>
            <>
              <MessageBar
                isOpen={isMessageBarOpen}
                message={messageString || null}
                onClose={() => {
                  dispatch(closeMessageBar());
                }}
                status={messageStatus}
              />
              <Routes>
                <Route element={<ProtectedRoute />}>
                  <Route
                    path="/questionnaire/:id"
                    element={<Questionnaire />}
                  />
                  <Route
                    path="*"
                    element={<Dashboard />}
                  />
                </Route>
              </Routes>
            </>
          </ThemeProvider>
        </Provider>
      </div>
    </RawIntlProvider>
  );
};

const AppWrapper = () => {
  return (
    <Router>
      <App />
    </Router>
  );
};

export default AppWrapper;
